import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart } from 'chart.js';
import { AnalyticsService } from '../../../../services/analytics.service';
import { Subscription } from 'rxjs';
import { formatCurrency, getLineChartConfig, getDoughnutChartConfig, PARKBOT_COLORS } from '../../../utils/chart-config';

@Component({
  selector: 'app-card-revenue-summary',
  templateUrl: './card-revenue-summary.component.html',
  styleUrls: ['./card-revenue-summary.component.css']
})
export class CardRevenueSummaryComponent implements OnInit, AfterViewInit, OnDestroy {
  revenueSummary: any = {
    totalRevenue: 0,
    netRevenue: 0,
    paidCount: 0,
    unpaidCount: 0,
    discountedCount: 0,
    discountSavings: 0,
    refundedCount: 0,
    refundedAmount: 0,
    cancelledCount: 0,
    dailyRevenue: {}
  };
  chart: Chart;
  subscription: Subscription;
  loadingSubscription: Subscription;
  dataLoaded = false;
  viewInitialized = false;
  chartInitAttempts = 0;
  maxChartInitAttempts = 5;
  isLoading = false;
  
  constructor(private analyticsService: AnalyticsService) { }

  ngOnInit(): void {
    // Subscribe to loading state
    this.loadingSubscription = this.analyticsService.loading$
      .subscribe(loading => {
        this.isLoading = loading;
      });

    this.subscription = this.analyticsService.getRevenueSummary()
      .subscribe(data => {
        // Ensure we have data and it's properly structured
        if (data) {
          this.revenueSummary = data;
          this.dataLoaded = true;
          this.tryInitChart();
        }
      });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    // Try to initialize chart if data is already loaded
    this.tryInitChart();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    
    if (this.chart) {
      this.chart.destroy();
    }
  }

  formatCurrency(amount: number): string {
    return formatCurrency(amount || 0);
  }

  private tryInitChart(): void {
    // Only proceed if both view is initialized and data is loaded
    if (!this.viewInitialized || !this.dataLoaded) {
      return;
    }

    // If chart already exists, update it
    if (this.chart) {
      this.updateChart();
      return;
    }

    // Try to initialize the chart with increasing delays if needed
    setTimeout(() => {
      if (!this.chart && this.chartInitAttempts < this.maxChartInitAttempts) {
        this.initChart();
        if (!this.chart) {
          // If chart creation failed, increment attempts and try again with longer delay
          this.chartInitAttempts++;
          this.tryInitChart();
        }
      }
    }, 300 * (this.chartInitAttempts + 1)); // Increase timeout for consecutive attempts
  }

  private initChart(): void {
    const canvas = document.getElementById('revenue-chart') as HTMLCanvasElement;
    if (!canvas) {
      console.error('Canvas element not found, will retry');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Canvas context not available');
      return;
    }

    // Prepare data for daily revenue trend
    const dailyRevenue = this.revenueSummary?.dailyRevenue || {};
    const sortedDates = Object.keys(dailyRevenue).sort();
    
    // If we don't have any data, create some sample data points
    // with zero values to show a blank chart rather than an error
    if (sortedDates.length === 0) {
      // Create 14 days of zero data
      const today = new Date();
      for (let i = 13; i >= 0; i--) {
        const date = new Date();
        date.setDate(today.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        dailyRevenue[dateKey] = 0;
      }
    }
    
    // Get the last 14 days of data or all if less than 14 days
    const allDates = Object.keys(dailyRevenue).sort();
    const displayDates = allDates.slice(-14);
    const labels = displayDates.map(date => {
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    });
    
    const revenueData = displayDates.map(date => dailyRevenue[date] || 0);
    
    // Calculate the refund proportion to estimate daily net revenue
    // Since we don't have daily refund data, we'll approximate using the total ratio
    const refundRatio = this.revenueSummary.totalRevenue > 0 ? 
      this.revenueSummary.refundedAmount / this.revenueSummary.totalRevenue : 0;
    
    // Estimate net revenue by applying the refund ratio to each day
    const netRevenueData = revenueData.map(amount => 
      Math.max(0, amount * (1 - refundRatio)));
    
    // Calculate 3-day moving average if we have enough data
    const movingAvgData = [];
    if (revenueData.length >= 3) {
      for (let i = 0; i < revenueData.length; i++) {
        if (i < 2) {
          movingAvgData.push(null); // No moving average for first two points
        } else {
          const avg = (revenueData[i] + revenueData[i-1] + revenueData[i-2]) / 3;
          movingAvgData.push(avg);
        }
      }
    }
    
    const datasets = [
      {
        label: 'Gross Revenue',
        backgroundColor: PARKBOT_COLORS.primary,
        borderColor: PARKBOT_COLORS.primary,
        data: revenueData,
        fill: false,
        tension: 0.1 // Add slight curve to lines
      },
      {
        label: 'Net Revenue (After Refunds)',
        backgroundColor: PARKBOT_COLORS.success,
        borderColor: PARKBOT_COLORS.success,
        data: netRevenueData,
        fill: false,
        tension: 0.1
      }
    ];
    
    // Add moving average if we have enough data
    if (movingAvgData.length > 0) {
      datasets.push({
        label: '3-Day Moving Avg',
        backgroundColor: 'rgba(0,0,0,0)',
        borderColor: PARKBOT_COLORS.info,
        borderDash: [5, 5],
        data: movingAvgData,
        fill: false,
        tension: 0.1
      } as any);
    }

    const config = getLineChartConfig(labels, datasets, 'Daily Revenue Trend');
    
    // Add custom tooltip to show currency format
    config.options.plugins.tooltip = {
      ...config.options.plugins.tooltip,
      callbacks: {
        label: function(context) {
          const label = context.dataset.label || '';
          const value = context.raw || 0;
          return `${label}: ${formatCurrency(value as number)}`;
        }
      }
    } as any;

    // Add options for better responsiveness
    config.options.responsive = true;
    config.options.maintainAspectRatio = false;
    
    // Ensure y-axis always starts at zero
    config.options.scales = {
      ...config.options.scales,
      y: {
        ...config.options.scales?.y,
        beginAtZero: true,
        ticks: {
          padding: 10,
          callback: function(value) {
            return formatCurrency(value as number);
          }
        } as any
      }
    } as any;
    
    try {
      this.chart = new Chart(ctx, config);
    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }

  private updateChart(): void {
    if (!this.chart) {
      this.initChart();
      return;
    }
    
    if (!this.revenueSummary) {
      return;
    }
    
    // Update chart data
    const dailyRevenue = this.revenueSummary.dailyRevenue || {};
    const sortedDates = Object.keys(dailyRevenue).sort();
    
    // If no data available, don't try to update
    if (sortedDates.length === 0) {
      return;
    }
    
    const displayDates = sortedDates.slice(-14);
    
    // Update labels
    this.chart.data.labels = displayDates.map(date => {
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    });
    
    // Update revenue data
    const revenueData = displayDates.map(date => dailyRevenue[date] || 0);
    this.chart.data.datasets[0].data = revenueData;
    
    // Update net revenue data
    const refundRatio = this.revenueSummary.totalRevenue > 0 ? 
      this.revenueSummary.refundedAmount / this.revenueSummary.totalRevenue : 0;
    const netRevenueData = revenueData.map(amount => 
      Math.max(0, amount * (1 - refundRatio)));
    
    // Ensure we have the net revenue dataset
    if (this.chart.data.datasets.length > 1) {
      this.chart.data.datasets[1].data = netRevenueData;
    }
    
    // Update moving average if it exists
    if (this.chart.data.datasets.length > 2 && revenueData.length >= 3) {
      const movingAvgData = [];
      for (let i = 0; i < revenueData.length; i++) {
        if (i < 2) {
          movingAvgData.push(null);
        } else {
          const avg = (revenueData[i] + revenueData[i-1] + revenueData[i-2]) / 3;
          movingAvgData.push(avg);
        }
      }
      this.chart.data.datasets[2].data = movingAvgData;
    }
    
    this.chart.update();
  }
}
